/**
 * HeatmapPanel (PULSE-211 P2) — Coinglass-style liquidation heatmap on a
 * standalone canvas, sitting below the chart over the same time window.
 *
 * X = time (old → new), Y = price (high → low), color = reconstructed
 * liquidation magnitude at that price level (aggregate-OI tier model,
 * PULSE-210). The reference price line is overlaid in white; the current
 * price is marked on the right axis.
 *
 * Self-contained 2D canvas (no chart-pixel coupling) so it's robust;
 * time/price axes are drawn from the payload. Redraws on data + resize,
 * DPR-aware for crisp cells.
 */
import { useEffect, useRef } from 'react'
import type { MarketHeatmap, Candle } from '@/hooks/useMarketApi'

const R_AXIS = 60   // right price-axis gutter (px)
const B_AXIS = 18   // bottom time-axis gutter (px)
const T_PAD = 4
const GAMMA = 1.25  // >1 darkens the low/mid end → purple base, bright magnets pop

// Viridis colormap — matches Coinglass: dark purple (low) → blue → teal →
// green → yellow (high). The purple base fills the map; only concentrated
// liquidation levels climb into green/yellow.
const STOPS: [number, [number, number, number]][] = [
  [0.0, [68, 1, 84]],
  [0.2, [59, 82, 139]],
  [0.4, [33, 145, 140]],
  [0.6, [60, 174, 110]],
  [0.8, [148, 215, 67]],
  [1.0, [253, 231, 37]],
]

function ramp(t: number): [number, number, number] {
  if (t <= 0) return STOPS[0][1]
  if (t >= 1) return STOPS[STOPS.length - 1][1]
  for (let i = 1; i < STOPS.length; i++) {
    if (t <= STOPS[i][0]) {
      const [a, ca] = STOPS[i - 1]
      const [b, cb] = STOPS[i]
      const f = (t - a) / (b - a || 1)
      return [
        Math.round(ca[0] + (cb[0] - ca[0]) * f),
        Math.round(ca[1] + (cb[1] - ca[1]) * f),
        Math.round(ca[2] + (cb[2] - ca[2]) * f),
      ]
    }
  }
  return STOPS[STOPS.length - 1][1]
}

function fmtPrice(p: number): string {
  if (p >= 1000) return p.toLocaleString('en-US', { maximumFractionDigits: 0 })
  if (p >= 1) return p.toFixed(2)
  return p.toPrecision(4)
}
function fmtTime(epoch: number): string {
  const d = new Date(epoch * 1000)
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    .replace(',', '')
}

export function HeatmapPanel({ data, candles }: { data: MarketHeatmap; candles?: Candle[] }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const wrap = wrapRef.current
    const canvas = canvasRef.current
    if (!wrap || !canvas || !data || data.empty) return

    const draw = () => {
      const cssW = wrap.clientWidth
      const cssH = wrap.clientHeight
      if (cssW < 10 || cssH < 10) return
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.round(cssW * dpr)
      canvas.height = Math.round(cssH * dpr)
      canvas.style.width = cssW + 'px'
      canvas.style.height = cssH + 'px'
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, cssW, cssH)

      const plotW = cssW - R_AXIS
      const plotH = cssH - B_AXIS - T_PAD
      const rows = data.grid.length
      const cols = rows ? data.grid[0].length : 0
      if (!rows || !cols) return

      const pmin = data.pmin ?? data.bin_prices[data.bin_prices.length - 1]
      const pmax = data.pmax ?? data.bin_prices[0]
      const span = (pmax - pmin) || 1
      const yOf = (price: number) => T_PAD + (pmax - price) / span * plotH
      const xOf = (ci: number) => (ci / cols) * plotW

      // background
      ctx.fillStyle = 'rgba(7,9,12,1)'
      ctx.fillRect(0, 0, cssW, cssH)

      // cells
      const cw = plotW / cols
      const ch = plotH / rows
      const lv = data.levels || 48
      for (let r = 0; r < rows; r++) {
        const y = T_PAD + r * ch
        const row = data.grid[r]
        for (let c = 0; c < cols; c++) {
          const v = row[c]
          const [rr, gg, bb] = ramp(Math.pow(v / lv, GAMMA))
          ctx.fillStyle = `rgb(${rr},${gg},${bb})`
          ctx.fillRect(xOf(c), y, cw + 0.6, ch + 0.6)
        }
      }

      // candles overlaid on the heatmap (Coinglass-style), mapped by time
      const t0e = data.times[0]
      const t1e = data.times[data.times.length - 1]
      const tSpan = (t1e - t0e) || 1
      const xOfTime = (t: number) => ((t - t0e) / tSpan) * plotW
      const inRange = (candles ?? []).filter((k) => k.t >= t0e && k.t <= t1e)
      if (inRange.length >= 2) {
        const dt = inRange[1].t - inRange[0].t
        const bw = Math.max(1.5, (dt / tSpan) * plotW * 0.7)
        for (const k of inRange) {
          const x = xOfTime(k.t)
          const up = k.c >= k.o
          const col = up ? '#26de81' : '#ff5c7c'
          ctx.strokeStyle = col
          ctx.fillStyle = col
          // wick
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(x, yOf(k.h)); ctx.lineTo(x, yOf(k.l)); ctx.stroke()
          // body
          const yo = yOf(k.o); const yc = yOf(k.c)
          const top = Math.min(yo, yc)
          const h = Math.max(1, Math.abs(yc - yo))
          ctx.fillRect(x - bw / 2, top, bw, h)
        }
      } else if (data.priceline.length) {
        // fallback: reference price line
        ctx.beginPath()
        ctx.strokeStyle = 'rgba(230,234,240,0.9)'
        ctx.lineWidth = 1
        data.priceline.forEach((pp, c) => {
          const x = xOf(c) + cw / 2
          const y = yOf(pp)
          if (c === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
        })
        ctx.stroke()
      }

      // current price marker + right axis
      ctx.font = '10px "JetBrains Mono", monospace'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = 'rgba(31,38,48,0.6)'
      ctx.fillRect(plotW, 0, R_AXIS, cssH)
      const ticks = 6
      ctx.strokeStyle = 'rgba(31,38,48,0.8)'
      ctx.fillStyle = '#8893A4'
      ctx.textAlign = 'left'
      for (let i = 0; i <= ticks; i++) {
        const price = pmax - (i / ticks) * span
        const y = yOf(price)
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotW, y); ctx.strokeStyle = 'rgba(31,38,48,0.35)'; ctx.stroke()
        ctx.fillText(fmtPrice(price), plotW + 5, y)
      }
      if (data.cur != null) {
        const y = yOf(data.cur)
        ctx.strokeStyle = '#FF4524'; ctx.setLineDash([3, 3]); ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotW, y); ctx.stroke(); ctx.setLineDash([])
        ctx.fillStyle = '#FF4524'
        ctx.fillRect(plotW, y - 7, R_AXIS, 14)
        ctx.fillStyle = '#07090C'
        ctx.fillText(fmtPrice(data.cur), plotW + 5, y)
      }

      // bottom time axis (a few labels)
      ctx.fillStyle = '#5A6373'
      ctx.textBaseline = 'alphabetic'
      const tTicks = Math.min(6, data.times.length)
      for (let i = 0; i < tTicks; i++) {
        const ci = Math.round((i / (tTicks - 1 || 1)) * (cols - 1))
        const x = xOf(ci)
        ctx.textAlign = i === 0 ? 'left' : i === tTicks - 1 ? 'right' : 'center'
        const tx = i === 0 ? 2 : i === tTicks - 1 ? plotW - 2 : x
        if (data.times[ci] != null) ctx.fillText(fmtTime(data.times[ci]), tx, cssH - 5)
      }
    }

    draw()
    const ro = new ResizeObserver(draw)
    ro.observe(wrap)
    return () => ro.disconnect()
  }, [data, candles])

  if (data?.empty) {
    return (
      <div className="grid place-items-center h-full text-textDim text-sm">
        Недостаточно данных OI для heatmap ({data.symbol}).
      </div>
    )
  }

  return (
    <div ref={wrapRef} className="w-full h-full">
      <canvas ref={canvasRef} />
    </div>
  )
}
